import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { uploadService, UploadProgress } from '../services/uploadService';
import { advancedPDFService } from '../services/advancedPDFService';
// import { pdfService } from '../services/pdfService'; // 暫時未使用
import { aiAnalysisService } from '../services/aiAnalysisService';
import { firestoreService, Policy } from '../services/firestoreService';
import { simpleContentPDFService } from '../services/simpleContentPDFService';
import { pdfPasswordService } from '../services/pdfPasswordService';
import PDFUnlockTipModal from './ui/PDFUnlockTipModal';
// import { ragChatService } from '../services/ragChatService'; // RAG 已移除，等待重新設計

interface PDFUploadProps {
  userId: string;
  onUploadComplete: (policy: Policy) => void;
  onError: (error: string) => void;
}

const PDFUpload: React.FC<PDFUploadProps> = ({ userId, onUploadComplete, onError }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [overallProgress, setOverallProgress] = useState<number>(0); // 整體進度 0-100
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // PDF解鎖錯誤提示模態框狀態
  const [showUnlockTipModal, setShowUnlockTipModal] = useState(false);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // 首先檢測PDF是否需要密碼
      setCurrentStep('檢測PDF密碼保護...');
      const passwordCheck = await pdfPasswordService.checkPasswordProtection(file);
      
      if (passwordCheck.isPasswordProtected && passwordCheck.needsPassword) {
        // PDF需要密碼保護，顯示錯誤提示模態框
        setCurrentStep('');
        setShowUnlockTipModal(true);
        // 重置文件輸入
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      } else if (passwordCheck.error) {
        throw new Error(passwordCheck.error);
      }
      
      // 不需要密碼，直接處理
      await processFileWithValidation(file);
    } catch (error: any) {
      console.error('文件處理失敗:', error);
      onError(error.message || '文件處理失敗');
      setCurrentStep('');
    }
  };


  // 統一的文件處理流程（用於有密碼和無密碼PDF）
  const processFileWithExtractedText = async (file: File, pdfText: string, uploadResult: any, encryptedPassword?: string): Promise<void> => {
    try {
      // 步驟1: AI分析 (75-85%)
      setCurrentStep('AI分析保單內容...');
      setOverallProgress(75);
      console.log('開始AI分析，文本長度:', pdfText.length);
      
      let analysisResult;
      try {
        analysisResult = await aiAnalysisService.analyzePolicyText(pdfText);
        console.log('AI分析完成:', analysisResult);
      } catch (error) {
        console.error('AI分析失敗:', error);
        // 如果AI分析失敗，使用默認值
        analysisResult = {
          policyName: file.name.replace('.pdf', ''),
          companyName: '未知保險公司',
          monthlyPremium: 0,
          annualPremium: 0,
          policyType: '綜合保險',
          policyNumber: 'N/A',
          effectiveDate: new Date().toISOString().split('T')[0],
          confidence: 0.5,
          premiumUnit: 'monthly',
          status: 'active'
        };
        console.log('使用默認AI分析結果:', analysisResult);
      }

      // 步驟2: 保存到Firestore (85-92%)
      setCurrentStep('保存保單信息...');
      setOverallProgress(85);
      console.log('開始保存到Firestore，用戶ID:', userId);
      
      // 使用AI直接返回的保費金額（已經是數字）
      const premiumUnit: 'monthly' | 'one-time' = (analysisResult.premiumUnit === 'one-time' ? 'one-time' : 'monthly');
      const monthlyPremium = analysisResult.monthlyPremium || 0;
      const annualPremium = analysisResult.annualPremium || (premiumUnit === 'one-time' ? monthlyPremium : monthlyPremium * 12);

      // 處理完整PDF文本：Firestore單文檔限制約1MB，所以截斷過長的文本
      // 保留前800KB的文本（約800,000字符），確保不超過Firestore限制
      const MAX_TEXT_LENGTH = 800000;
      const truncatedText = pdfText.length > MAX_TEXT_LENGTH 
        ? pdfText.substring(0, MAX_TEXT_LENGTH) + '\n\n[文本已截斷，完整內容請參考原始PDF文件]'
        : pdfText;

      const policyData: Partial<Policy> = {
        name: analysisResult.policyName,
        company: analysisResult.companyName,
        monthlyPremium: monthlyPremium,
        annualPremium: annualPremium,
        category: analysisResult.policyType || '綜合保險',
        policyNumber: analysisResult.policyNumber,
        effectiveDate: analysisResult.effectiveDate,
        ...(analysisResult.expiryDate && { expiryDate: analysisResult.expiryDate }), // 新增：只有當到期日期存在時才保存
        pdfUrl: uploadResult.downloadURL,
        fileName: uploadResult.fileName,
        filePath: uploadResult.filePath,
        fileSize: uploadResult.fileSize,
        uploadTime: uploadResult.uploadTime,
        // 簡化analysisResult，只保留必要的調試資訊（移除重複字段）
        analysisResult: {
          confidence: analysisResult.confidence,
          extractionMethod: 'openai'
        },
        confidence: analysisResult.confidence,
        description: `AI分析可信度: ${Math.round(analysisResult.confidence * 100)}%`,
        // 新增保費單位和狀態字段
        premiumUnit: premiumUnit,
        status: (analysisResult.status || 'active') as 'active' | 'expired',
        // 新增：保存完整PDF文本用於AI回答問題
        fullText: truncatedText
      };

      console.log('準備保存的保單數據:', policyData);
      console.log('AI分析結果詳細信息:');
      console.log('- 保單名稱:', analysisResult.policyName);
      console.log('- 保險公司:', analysisResult.companyName);
      console.log('- 月繳保費:', analysisResult.monthlyPremium);
      console.log('- 保單類型:', analysisResult.policyType);
      console.log('- 可信度:', analysisResult.confidence);

      const policyId = await firestoreService.saveUserPolicy(userId, policyData);
      console.log('✅ Firestore保存成功，保單ID:', policyId);
      console.log('✅ 保存的保單數據:', policyData);

      // 創建完整的Policy對象
      const completePolicy: Policy = {
        id: policyId,
        name: policyData.name || '未知保單',
        description: policyData.description || '',
        createdAt: new Date(),
        updatedAt: new Date(),
        ...policyData
      } as Policy;

      // 步驟3: 生成向量並存儲到向量數據庫（RAG）(92-100%)
      if (truncatedText && truncatedText.trim().length > 0) {
        // RAG 向量索引生成已移除，等待重新設計 AI 對答邏輯
        // try {
        //   setCurrentStep('生成向量索引...');
        //   setOverallProgress(92);
        //   console.log('開始為保單生成向量索引...');
        //   await ragChatService.processPolicyForRAG(completePolicy);
        //   console.log('✅ 向量索引生成完成');
        //   setOverallProgress(98);
        // } catch (error) {
        //   console.error('⚠️ 向量索引生成失敗（不影響保單上傳）:', error);
        //   // 向量生成失敗不影響保單上傳，只記錄錯誤
        //   setOverallProgress(98);
        // }
        setOverallProgress(98);
      } else {
        console.warn('⚠️ 保單文本為空，跳過向量索引生成');
        setOverallProgress(98);
      }

      // 步驟4: 完成上傳
      setCurrentStep('上傳完成');
      setOverallProgress(100);
      onUploadComplete(completePolicy);
      console.log('PDF上傳完成:', completePolicy);

    } catch (error: any) {
      console.error('處理文件失敗:', error);
      onError(error.message || '處理文件失敗');
    }
  };



  // 帶驗證的文件處理流程
  const processFileWithValidation = async (file: File): Promise<void> => {
    try {
      setUploading(true);
      setCurrentStep('驗證PDF文件...');

      // 步驟1: 使用簡化版PDF驗證
      const validationResult = await simpleContentPDFService.validatePDF(file);
      
      if (!validationResult.isValid) {
        // 顯示錯誤信息
        const errorMessage = validationResult.error || '文件驗證失敗';
        alert(errorMessage);
        onError(errorMessage);
        return;
      }

      // 驗證通過，繼續處理
      await processFile(file);

    } catch (error: any) {
      console.error('文件驗證失敗:', error);
      onError(error.message || '文件驗證失敗');
    } finally {
      setUploading(false);
      setUploadProgress(null);
      setCurrentStep('');
      // 重置文件輸入
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // 處理已驗證的文件
  const processFile = async (file: File, extractedText?: string): Promise<void> => {
    try {
      setUploading(true);
      setOverallProgress(0);
      setCurrentStep('準備上傳...');

      // 步驟1: 上傳文件到Firebase Storage (0-60%)
      setCurrentStep('上傳PDF文件...');
      const uploadResult = await uploadService.uploadPDF(
        file,
        userId,
        (progress) => {
          // 將上傳進度映射到 0-60% 的範圍
          const mappedProgress = Math.round(progress.progress * 0.6);
          setOverallProgress(mappedProgress);
          setUploadProgress(progress);
          setCurrentStep(`上傳中... ${progress.progress}%`);
        }
      );

      // 上傳完成，設置為 60%
      setOverallProgress(60);
      // 保持最後的上傳進度狀態，不要覆蓋它（因為它已經顯示100%了）

      // 步驟2: 提取PDF文本 (60-75%)
      setCurrentStep('處理PDF文本...');
      setOverallProgress(65);
      
      let pdfText = '';
      try {
        const pdfTextResult = await advancedPDFService.extractTextFromFile(file);
        pdfText = pdfTextResult.text || '';
        console.log('PDF文本提取完成，文本長度:', pdfText.length);
        console.log('提取方法:', pdfTextResult.extractionMethod);
        console.log('提取可信度:', pdfTextResult.confidence);
        
        if (pdfText && pdfText.trim().length > 0) {
          console.log('✅ PDF文本提取成功，文本預覽 (前500字符):', pdfText.substring(0, 500));
        } else {
          console.warn('⚠️ PDF文本提取結果為空');
        }
      } catch (error) {
        console.error('❌ PDF文本提取失敗:', error);
        // 不設置錯誤訊息，保持為空字符串，這樣 fullText 就不會包含錯誤訊息
        pdfText = '';
      }
      
      // 如果文本為空或太短（可能是提取失敗），記錄警告但不設置錯誤訊息
      if (!pdfText || pdfText.trim().length < 10) {
        console.warn('⚠️ PDF文本為空或太短，可能無法用於AI分析');
        // 保持為空，不要設置錯誤訊息，這樣 fullText 就是空的
        pdfText = '';
      }

      // 步驟3: 使用與有密碼PDF完全相同的處理流程
      await processFileWithExtractedText(file, pdfText, uploadResult);

    } catch (error: any) {
      console.error('文件處理失敗:', error);
      onError(error.message || '文件處理失敗');
    } finally {
      // 延遲一下再清除，讓用戶看到100%完成
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(null);
        setOverallProgress(0);
        setCurrentStep('');
        // 重置文件輸入
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 1000);
    }
  };

  const handleUploadClick = () => {
    // 顯示解鎖提示模態框（提醒用戶先解鎖PDF）
    setShowUnlockTipModal(true);
  };
  
  const handleContinueUpload = () => {
    // 關閉提示模態框並觸發文件選擇
    setShowUnlockTipModal(false);
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleFileSelect}
        className="hidden"
      />

      <Button
        onClick={handleUploadClick}
        disabled={uploading}
        className="w-full"
        style={{
          background: uploading ? 'var(--medium-gray)' : 'var(--charcoal)',
          border: 'none',
          borderRadius: '12px'
        }}
      >
        {uploading ? '處理中...' : '上傳PDF保單'}
      </Button>

      {uploading && (
        <div className="space-y-2">
          <div className="text-sm text-gray-600 text-center">
            {currentStep}
          </div>
          
          {/* 進度條顯示 */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
          
          <div className="text-xs text-gray-500 text-center">
            {uploadProgress && uploadProgress.totalBytes > 0 ? (
              <>
                {uploadService.formatFileSize(uploadProgress.bytesTransferred)} / {uploadService.formatFileSize(uploadProgress.totalBytes)}
              </>
            ) : (
              <span>處理中...</span>
            )}
          </div>
        </div>
      )}

      {/* PDF解鎖錯誤提示模態框 */}
      <PDFUnlockTipModal
        isOpen={showUnlockTipModal}
        onClose={() => setShowUnlockTipModal(false)}
        onContinue={handleContinueUpload}
      />
    </div>
  );
};

export default PDFUpload;
